/**
 * tutorStudentConversationAdapter — la conversation du tuteur avec un de ses élèves : quel fil, et
 * quelles activités en sont nées.
 *
 * Port de la branche tuteur de `common/messenger/ChatScreen.js` (`studentId` dans la route) et de
 * `CommonChat.renderMessageImage` / `renderMessagePdf` (tuteur, `currentUser.id !== 0`), sur les mêmes
 * champs. Le fil lu, lui, se traduit par l'adaptateur de l'élève (`toUIConversationMessages`) : la
 * messagerie sert les mêmes messages des deux côtés.
 */
import { type ApiChatActivity, idOf, sessionThreadId } from "../../student/Messaging/studentConversationAdapter"
import { type UIMessageAttachment } from "../../../ui/types/studentConversation"

type Id = string | number

/** Une séance du compte du tuteur : ses élèves, et le fil privé avec chacun (par id d'élève). */
export type ApiTutorConversationSession = {
    students?: ({ id?: Id | null; tutor_messenger_thread_id?: Id | null } | null)[] | null
    thread_ids?: Record<string, Id | null | undefined> | null
}

/** Le compte du tuteur, ce que la conversation en lit. */
export type ApiTutorConversationAccount = {
    id?: Id
    display_name?: string | null
    live_sessions?: (ApiTutorConversationSession | null)[] | null
    /** Les messages lus, par fil (`getMessages` / `getPageMessages`, `currentUser.js`). */
    messages?: Record<string, unknown> | null
}

/** L'élève, ce que la conversation en lit : son fil avec son tuteur, et ses activités. */
export type ApiTutorConversationStudent = {
    id?: Id | null
    tutor_messenger_thread_id?: Id | null
    todo_activities?: ApiChatActivity[] | null
    activities?: ApiChatActivity[] | null
}

/**
 * Le fil avec l'élève — `ChatScreen.js:52-69` : les fils que les séances du tuteur nomment
 * (`live_sessions[].thread_ids`, fusionnés dans l'ordre des séances), pour cet élève ; à défaut, le fil
 * que l'élève a avec son tuteur (`tutor_messenger_thread_id`), lu sur l'élève de la liste du tuteur,
 * sinon sur celui d'une séance (le legacy fusionnait les deux, la liste l'emportant).
 */
export function tutorStudentThreadId(
    account: ApiTutorConversationAccount | null | undefined,
    student: ApiTutorConversationStudent | null | undefined,
    studentId: Id | null | undefined
): string | null {
    const wanted = idOf(studentId)
    if (wanted == null) return null
    const sessions = (account?.live_sessions ?? []).filter((s): s is ApiTutorConversationSession => s != null)
    const fromSessions = sessionThreadId(sessions, wanted)
    if (fromSessions != null) return fromSessions
    const own = idOf(student?.tutor_messenger_thread_id)
    if (own != null) return own
    const sessionStudent = sessions
        .flatMap((session) => session.students ?? [])
        .find((candidate) => idOf(candidate?.id) === wanted)
    return idOf(sessionStudent?.tutor_messenger_thread_id)
}

/** Un fichier que le tuteur regarde avant d'en tirer une activité (l'aperçu). */
export type TutorChatMedia = {
    kind: "image" | "document"
    uri: string
    name?: string | null
    mimeType?: string | null
    /**
     * Le message d'où il vient, s'il est déjà au serveur : l'activité créée s'y rattache (`message_id`).
     * Absent : un fichier neuf, ou un envoi encore local. Posé par l'aperçu (`useTutorMediaPreviewState`).
     */
    messageId?: string | null
}

/**
 * Une pièce du fil → ce que l'aperçu montre, sous les noms du legacy : une photo `image_<id>`, un
 * document par son nom, sinon `document_<id>.pdf` (`renderMessageImage`, `renderMessagePdf`).
 */
export function chatMediaOf(
    messageId: string,
    attachment: UIMessageAttachment | null | undefined
): TutorChatMedia | null {
    if (attachment?.kind === "image") return { kind: "image", uri: attachment.uri, name: `image_${messageId}` }
    if (attachment?.kind === "document") {
        return { kind: "document", uri: attachment.uri, name: attachment.name ?? `document_${messageId}.pdf` }
    }
    return null
}

/** Le type que « Créer une activité » donne au fichier (`handleCreateActivityFromPreview`). */
export function generationMimeType(media: TutorChatMedia): string {
    return media.kind === "document" ? "application/pdf" : "image/png"
}
